import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom'
import { DateTime } from 'luxon';

// Material-Ui Components
import Paper from '@material-ui/core/Paper';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import ViewIcon from '@material-ui/icons/KeyboardArrowRight';
import AcceptIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import Pagination from '@material-ui/lab/Pagination';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';


// Local imports
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';


const useRequestStyles = makeStyles(theme => ({
    paper: {
        marginTop: theme.spacing(3)
    },
    tableCell: {
        whiteSpace: `nowrap`,
    },
    tableCellNotes: {
        whiteSpace: `nowrap`,
        overflow: `hidden`,
        textOverflow: `ellipsis`,
        maxWidth: '1px',
        minWidth: '1px',
        width: `100%`
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    pagination: {

    },
    footer: {
        margin: theme.spacing(3),
    },
    link: {
        textDecoration: "none"
    }
}));

export default function ResignationRequests(props) {

    const { limit } = props

    const [ requests, setRequests ] = useState([]);
    const [ requestsLoading, setRequestsLoading ] = useState(false);
    const [ page, setPage ] = useState(1);
    const [ verifyApproveResignationOpen , setVerifyApproveResignationOpen ] = useState(false);
    const [ activeRequestID, setActiveRequestID ] = useState(0);

    const classes = useRequestStyles();
    const { app } = useApp();
    const api = useApi();
    const rows = limit ? 5 : 10;
    const totalRequests = requests ? requests.length : 0
    


    const getRequests = () => {
        setRequestsLoading(true);
        api.get('/admin/club/resignation-requests')
            .then(res => {
                // console.log(res)
                setRequests(res.requests);
                })
            .then(() => setRequestsLoading(false));
    }

    useEffect(() => {
        getRequests();
    }, []);

    const postApprove = () => {
        setRequestsLoading(true);
        api.post(`/admin/club/approve-resignation/`, {id: activeRequestID})
            .then(() => {
                setVerifyApproveResignationOpen(false);
                getRequests();
            })
            .finally(() => setRequestsLoading(false))
    }

    const postDeny = (id) => {
        setRequestsLoading(true);
        api.post(`/admin/club/deny-resignation/`, {id: id})
            .then(() => getRequests())
            .finally(() => setRequestsLoading(false))
    }

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Dialog Functions

    const verifyApproveRequest = (id) => {
        setVerifyApproveResignationOpen(true);
        setActiveRequestID(id);
    }

    const handleApproveRequestCancel = () => {
        setVerifyApproveResignationOpen(false);
        setActiveRequestID(0);
    }


    return (
        <>
        <Paper className={classes.paper}>
        <Loading isLoading={requestsLoading}/>
            {requests && requests.length ? (
                <>
                <Table aria-label="Access Requests Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Member Name</TableCell>
                            <TableCell>Requested to Resign On</TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.slice(((page-1)*rows), (((page-1)*rows) + rows)).map((request) => (
                            <TableRow key={request.id}>
                                <TableCell component="th" scope="row" className={classes.tableCell}>{request.user.full_name}</TableCell>
                                <TableCell className={classes.tableCell}>{DateTime.fromSQL(request.meta.member_resigned_at).toLocaleString(DateTime.DATE_MED)}</TableCell>
                                <TableCell/>
                                <>
                                <TableCell align="right" padding="none" className={classes.tableCell}>
                                <Tooltip title="Approve Request">
                                    <IconButton 
                                        aria-label="Approve"
                                        onClick={() => verifyApproveRequest(request.id)}
                                    >
                                        <AcceptIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Deny Request">
                                    <IconButton 
                                        aria-label="Deny"
                                        onClick={() => postDeny(request.id)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="View Details">
                                    <IconButton aria-label="Open" component={RouterLink} to={`/admin/resignation-requests/${request.id}`}>
                                        <ViewIcon />
                                    </IconButton>
                                </Tooltip>
                                </TableCell>
                                </>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {(limit && requests.length > 5) && 
                        <Grid item xs={12}container justify="flex-end" alignItems="center">
                            <Typography color="textSecondary" variant="subtitle1" className={classes.footer}>
                                <Link
                                    color="secondary" 
                                    component={RouterLink} 
                                    to="/admin/resignation-requests" 
                                    >
                                    You have {totalRequests} total requests. View All
                                </Link>
                            </Typography>
                        </Grid>
                }
                {!limit && requests.length &&
                    <Grid item xs={12}container justify="center" alignItems="center">
                        <Pagination 
                            className={classes.footer}
                            count={Math.ceil((requests.length) / rows)}
                            page={page}
                            onChange={handlePageChange} 
                        />
                    </Grid>
                }
                </>
            ) : (
                <Typography align="center" color="textSecondary" variant="subtitle2" className={classes.empty}>
                    No resignation requests.
                </Typography>
            )}
        </Paper>
        <Dialog
            open={verifyApproveResignationOpen}
            onClose={handleApproveRequestCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            >
            <DialogTitle id="alert-dialog-title">Confirm User Resignation</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    This action will remove any content this user has created and will disable in-app communications. Administrators will no longer be able to access their details and manage their membership. Are you sure you want to accept this resignation request?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleApproveRequestCancel} color="primary" autoFocus>
                    Cancel
                </Button>
                <RedButton onClick={postApprove} variant="contained">
                    Approve Resignation
                </RedButton>
            </DialogActions>
        </Dialog>
        </>
    )
}